/*  MUI  */
import {
    Box,
    CircularProgress,
    Skeleton,
    TablePagination,
    Typography
} from '@mui/material';

/*  React  */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

const PaginationTotalCount = ({ count }) =>
    count >= 0 ? (
        <Typography variant="span" sx={{ marginLeft: '4px' }}>
            {count}
        </Typography>
    ) : (
        <CircularProgress size={12} sx={{ marginLeft: '8px' }} />
    );

export const ManagedTablePagination = ({
    apiTable,
    disabled,
    disableFirstAndLastPageButtons,
    isSearching,
    page,
    renderCount,
    rowCount,
    rowData,
    rowsApiPreload,
    rowsPerPage,
    setPage,
    setRowsPerPage
}) => {
    const { t } = useTranslation();

    // MUI-approved
    const rowsPerPageOptions = [
        2,
        5,
        10,
        25,
        50,
        100,
        { label: t('Common.all'), value: -1 }
    ];

    const paginationOnRowsPerPageChange = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleChangePage = (_e, newPage) => {
        setPage(newPage);
    };

    // rowCount is all rows, renderCount is only a subset of rowCount, it is the lines being rendered.
    const count =
        apiTable && !isSearching && !rowsApiPreload ? rowCount : renderCount;

    const labelDisplayedRows = () => {
        const labels = {
            from: count === null || count === 0 ? 0 : page * rowsPerPage + 1,
            to:
                count > 0
                    ? Math.min(count, (page + 1) * rowsPerPage)
                    : Math.min(
                          (page + 1) * rowsPerPage,
                          (rowData?.length || 0) + page * rowsPerPage
                      )
        };
        return (
            <Box as="span" display="flex" alignItems="center">
                <Trans
                    i18nKey="Pagination.fromToCount"
                    components={{
                        Count: <PaginationTotalCount count={count} />
                    }}
                    values={{ from: labels.from, to: labels.to }}
                />
            </Box>
        );
    };

    return apiTable && !rowData && !rowData?.length ? (
        <Box width="33%">
            <Skeleton height="32px" animation="wave" />
        </Box>
    ) : (
        <TablePagination
            component="div"
            showFirstButton={!disableFirstAndLastPageButtons}
            showLastButton={!disableFirstAndLastPageButtons}
            count={count ?? -1}
            labelDisplayedRows={labelDisplayedRows}
            data-testid="managedTableTablePagination"
            disabled={disabled}
            onPageChange={handleChangePage}
            onRowsPerPageChange={(e) => paginationOnRowsPerPageChange(e)}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            slotProps={{
                select: { inputProps: { 'aria-label': 'rows per page' } }
            }}
        />
    );
};
